// @ts-nocheck

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  StudyCompanyDataResponse,
  AllStudyCompanyListResponse,
  Study,
  StudyHistoryRequest,
  StudyHistoryChartData,
  PatchStudyRequest,
  StudyDetail,
  StudyYearCompanyV2,
  StudyYearRequest,
  StudyYearProfileV2,
  CategoryRequest,
  CategoryData,
  StudyCreate,
  StudyUpdateRequest,
  StudyDataRequest,
} from '@/types/studies';
import { studiesService } from '../../services/studies.service';
import { AppDispatch, RootState } from '../index';
import { showSnackbar } from '../snakebar/slice';

export const studies = {
  // --------------------------------------- V3 - MIGRATED ------------------------------------------

  getAllStudies: createAsyncThunk(
    'studies/getAllStudies',
    async (params: GetStudiesParams | undefined, { rejectWithValue }) => {
      try {
        const response = await studiesService.getAllStudies(params);
        // Return pagination data format
        return {
          data: Array.isArray(response) ? response : response.data,
          total: Array.isArray(response) ? response.length : response.total,
          offset: params?.offset || 0,
        };
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch studies');
      }
    }
  ),

  getStudyProfile: createAsyncThunk<StudyDetail, string>(
    'studies/getStudyProfile',
    async (study_id, { rejectWithValue }) => {
      try {
        const response = await studiesService.getStudyProfile(study_id);
        return response;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Data not Found');
      }
    }
  ),

  updateStudy: createAsyncThunk<StudyDetail, { studyId: string; data: StudyUpdateRequest }>(
    'studies/updateStudy',
    async ({ studyId, data }, { rejectWithValue }) => {
      try {
        const response = await studiesService.updateStudy(studyId, data);
        return response;
      } catch (error: any) {
        return rejectWithValue(error.response?.data.message || 'Failed to update study');
      }
    }
  ),

  createStudy: createAsyncThunk<
    StudyDetail,
    StudyCreate,
    { dispatch: AppDispatch; state: RootState }
  >('studies/createStudy', async (studyData, { dispatch, rejectWithValue }) => {
    try {
      const response = await studiesService.createStudy(studyData);
      dispatch(
        showSnackbar({
          message: 'Successfully created study!',
          variant: 'success',
        })
      );
      return response;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error instanceof Error ? error.message : 'Failed to create study',
          variant: 'error',
        })
      );
      return rejectWithValue(error.response?.data?.message || 'Failed to create study');
    }
  }),

  deleteStudy: createAsyncThunk<void, string>(
    'studies/deleteStudy',
    async (studyId, { rejectWithValue }) => {
      try {
        await studiesService.deleteStudy(studyId);
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Failed to delete study');
      }
    }
  ),

  getStudyData: createAsyncThunk<any[], StudyDataRequest>(
    'studies/getStudyData',
    async ({ study_id, filters }, { rejectWithValue }) => {
      try {
        const response = await studiesService.getStudyData(study_id, filters);
        return response;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch study data');
      }
    }
  ),

  // --------------------------------------- V2 - LEGACY SUPPORT ------------------------------------------

  // getStudyYearProfileV2: createAsyncThunk<StudyYearProfileV2, StudyYearRequest>(
  //   'studies/getStudyYearProfileV2',
  //   async ({ study_name, year }, { rejectWithValue }) => {
  //     try {
  //       const response = await studiesService.getStudyYearProfileV2(study_name, year);
  //       return response;
  //     } catch (error: any) {
  //       return rejectWithValue(error.response?.data?.message || 'Data not found');
  //     }
  //   }
  // ),

  getStudyYearCompaniesV2: createAsyncThunk<StudyYearCompanyV2[], StudyYearRequest>(
    'studies/getStudyYearCompanies',
    async ({ study_name, year }, { rejectWithValue }) => {
      try {
        const response = await studiesService.getStudyYearCompanies(study_name, year);
        return response;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Data not found');
      }
    }
  ),

  getCategoryMarketData: createAsyncThunk<CategoryData[], CategoryRequest>(
    'studies/getCategoryHistory',
    async (param: CategoryRequest, { rejectWithValue }) => {
      try {
        const response = await studiesService.getCategoryMarketData(
          param.study_name,
          param.year,
          param.segment_name,
          param.category_name,
          param.include_peers
        );
        return response;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch study history');
      }
    }
  ),

  // --------------------------------------- MISSING METHODS (TO BE ADDED TO SERVICE) ------------------------------------------

  getStudiesCompaniesData: createAsyncThunk<StudyCompanyDataResponse, string>(
    'studies/getStudiesCompaniesData',
    async (study_id, { rejectWithValue }) => {
      try {
        // TODO: Add this method to studiesService
        const response = await studiesService.getStudiesCompaniesData(study_id);
        return response;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Data not found');
      }
    }
  ),

  getStudyCompanies: createAsyncThunk<AllStudyCompanyListResponse, string>(
    'studies/getStudyCompanies',
    async (studyId, { rejectWithValue }) => {
      try {
        // TODO: Add this method to studiesService
        const response = await studiesService.getStudyCompanies(studyId);
        return response;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Data not found');
      }
    }
  ),

  getStudyHistory: createAsyncThunk<StudyHistoryChartData, StudyHistoryRequest>(
    'studies/getStudyHistory',
    async (param: StudyHistoryRequest, { rejectWithValue }) => {
      try {
        // TODO: Add this method to studiesService
        const response = await studiesService.getStudyHistory(
          param.study_id,
          param.category_id,
          param.with_peers
        );
        return response;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch study history');
      }
    }
  ),
};
